import Checkbox from '@alexis/components/Checkbox';
import { clsx } from '@alexis/helpers/clsx';
import { translateNumber, translateText } from '@alexis/helpers/translation';
import SegmentView from '@pages/FlightSearchResult/components/TripCard/LegView/SegmentView';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { getImageTransformationsUrl } from '@helpers/imageTransformations';

import MoonIcon from '@icons/crescent_moon_filled.svg';
import TimeIcon from '@icons/time.svg';

import { getIsRtlState, getLocaleState, getTranslationsState } from '@redux/selectors';

import type { LegViewModel } from '@wegoTypes/flights/legViewModel';

import styles from './LegView.module.scss';

export interface LegViewProps {
  index: number;
  isCardHovered: boolean;
  isLegChecked: boolean;
  isOfferEngine?: boolean;
  isFareCache?: boolean;
  isSponsoredAd?: boolean;
  legViewModel: LegViewModel;
  tripViewModelLegLength: number;
  onAirportCodeClick(searchParam: 'origin' | 'destination', airportCode: string): void;
  onLegToggle(index: number, legId: string): void;
}

const LegView: React.FC<LegViewProps> = ({
  index,
  isCardHovered,
  isLegChecked,
  isOfferEngine,
  isFareCache,
  isSponsoredAd,
  legViewModel,
  tripViewModelLegLength,
  onAirportCodeClick,
  onLegToggle,
}) => {
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);
  const isRtl = useSelector(getIsRtlState);

  const { isMultiCity, isRoundTrip } = useFlightSearchPageParams();

  const handleOnAirportCodeClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>,
      searchParam: 'origin' | 'destination',
      airportCode: string,
    ): void => {
      if (!isMultiCity) {
        e.stopPropagation();
        onAirportCodeClick(searchParam, airportCode);
      }
    },
    [isMultiCity, onAirportCodeClick],
  );

  const legViewAirlines = useMemo(() => {
    return uniqBy(legViewModel.airlines, 'name');
  }, [legViewModel.airlines]);

  return (
    <div
      data-pw='tripCard_legDetails'
      data-testid='leg-view'
      data-index={index}
      key={legViewModel.leg.id}
      className={clsx(
        styles.leg,
        isRtl && styles.rtl,
        isCardHovered && styles.cardHovered,
        isOfferEngine && styles.isOfferEngine,
        isFareCache && styles.isFareCache,
      )}
    >
      {/* Section: Airline Information */}
      <div data-testid='leg_view_airline_images' className={styles.images}>
        {legViewAirlines.slice(0, 3).map((airline, index) => (
          <img
            key={airline.code}
            data-testid='leg-airline-image'
            alt={airline?.code}
            className={index === 0 ? styles.mainImage : styles.subImage}
            src={getImageTransformationsUrl(
              `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${airline?.code}.png`,
            )}
          />
        ))}

        {legViewAirlines.length > 3 ? (
          <img
            className={styles.subImage}
            src={getImageTransformationsUrl(
              `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/kabru/multiple-airlines.png`,
            )}
          />
        ) : null}
      </div>

      <span data-pw='leg_airlineName' data-testid='leg-airline-name' className={styles.airlineName}>
        {legViewAirlines[0]?.name}
      </span>

      {/* Section: Filter by trips */}
      <div
        data-pw='leg_filterCheckBox'
        className={styles.tooltipContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {tripViewModelLegLength > 1 && !isSponsoredAd ? (
          <Checkbox
            data-testid='leg_filterCheckBox'
            className={styles.checkbox}
            isChecked={isLegChecked}
            onClick={() => onLegToggle(index, legViewModel.leg.id)}
          />
        ) : null}

        <div className={clsx(styles.tooltip, styles.bottom)}>{translations.show_result}</div>
      </div>

      {/* Section: Time information */}
      <div className={styles.information}>
        <div className={styles.timeContainer}>
          <div data-pw='leg_scheduleInfo' className={styles.timing}>
            <div className={styles.time} data-testid='depart-time'>
              {legViewModel.leg.departureTime}
            </div>

            <div className={styles.totalDuration} data-testid='duration'>
              <TimeIcon className={styles.icon} />

              {legViewModel.leg.duration}
            </div>

            <div className={styles.time} data-testid='arrive-time'>
              {legViewModel.leg.arrivalTime}
            </div>
          </div>

          <div className={styles.timeLine}>
            <div className={styles.start}>
              <div className={styles.point}></div>

              <div className={styles.tooltipContainer}>
                <div
                  data-pw='leg_departureAirportCode'
                  data-testid='leg-departure-airport-code'
                  className={clsx(styles.airportCode, !isMultiCity && styles.filterByAirportCode)}
                  onClick={(e) =>
                    handleOnAirportCodeClick(
                      e,
                      index === 1 && isRoundTrip ? 'destination' : 'origin',
                      legViewModel.leg.departureAirportCode,
                    )
                  }
                >
                  {legViewModel.leg!.departureAirportCode}
                </div>

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  {!isMultiCity
                    ? translations.show_only_flights
                    : legViewModel.departureAirport?.name}
                </div>
              </div>
            </div>

            {legViewModel.segmentViewModels.map((segmentViewModel, index) => {
              return <SegmentView key={index} segmentViewModel={segmentViewModel} />;
            })}

            {legViewModel.segmentViewModels.length === 0 ? (
              <div className={styles.direct}>{translations.direct}</div>
            ) : null}

            <div className={styles.end}>
              <div className={styles.point}></div>

              <div className={styles.tooltipContainer}>
                <div
                  data-pw='leg_arrivalAirportCode'
                  data-testid='leg-arrival-airport-code'
                  className={clsx(styles.airportCode, !isMultiCity && styles.filterByAirportCode)}
                  onClick={(e) =>
                    handleOnAirportCodeClick(
                      e,
                      index === 1 && isRoundTrip ? 'origin' : 'destination',
                      legViewModel.leg.arrivalAirportCode,
                    )
                  }
                >
                  {legViewModel.leg.arrivalAirportCode}
                </div>

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  {!isMultiCity
                    ? translations.show_only_flights
                    : legViewModel.arrivalAirport?.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Overnight flight information */}
      <div data-pw='leg_overNightFlightAndNoOfDaysInfo' className={styles.overnight}>
        {legViewModel.leg.overnight ? (
          <>
            <div className={styles.tooltipContainer}>
              <div className={styles.nightCount} data-testid='leg-overnight'>
                {legViewModel.leg.durationDays > 0 ? '+' : ''}
                {translateNumber(legViewModel.leg.durationDays, locale === 'fa')}
              </div>

              <div className={clsx(styles.tooltip, styles.bottom)}>
                {translateText(
                  translations.flight_land,
                  locale,
                  legViewModel.leg.durationDays === 1 ? 0 : legViewModel.leg.durationDays,
                )}
              </div>
            </div>

            <div className={styles.tooltipContainer}>
              <MoonIcon className={styles.icon} />

              <div className={clsx(styles.tooltip, styles.bottom)}>
                {translations.overnight_flight}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LegView;
